import { createClient } from '@supabase/supabase-js'

type ViteEnvironment = Record<string, string | undefined>

export type SupabaseDiagnosticError = {
  code?: string
  message?: string
  details?: string
  hint?: string
  status?: number
}

const viteEnvironment = (import.meta.env ?? {}) as ViteEnvironment

const supabaseUrl =
  viteEnvironment.VITE_SUPABASE_URL ??
  'https://koobgllhtexqpihdmxwm.supabase.co'

const supabasePublishableKey =
  viteEnvironment.VITE_SUPABASE_PUBLISHABLE_KEY ??
  'sb_publishable_UMGzYPIYfo0xOXBM0j1bWw_SYsqKNvN'

export const supabaseConfigurationError =
  'Supabase no está configurado correctamente. Contactá al administrador.'

export const isSupabaseConfigured = Boolean(
  supabaseUrl && supabasePublishableKey,
)

export const supabase = createClient(supabaseUrl, supabasePublishableKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
})

export function buildSupabaseRestUrl(resource: string, query = '') {
  return `${supabaseUrl}/rest/v1/${resource}${query ? `?${query}` : ''}`
}

export function buildSupabaseRpcUrl(functionName: string) {
  return `${supabaseUrl}/rest/v1/rpc/${functionName}`
}

export function buildSupabaseAuthUrl(path: string) {
  return `${supabaseUrl}/auth/v1/${path.replace(/^\/+/, '')}`
}

export type SupabaseRequestDiagnosticOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  requestPayload?: unknown
  responseBody?: unknown
  stage?: string
  sessionUserId?: string | null
  profileId?: string | null
  profileActive?: boolean | null
}

export function logSupabaseRequestDiagnostic(
  resource: string,
  url: string,
  error: SupabaseDiagnosticError | null | undefined,
  options: SupabaseRequestDiagnosticOptions = {},
) {
  const payload = {
    stage: options.stage ?? null,
    method: options.method ?? 'GET',
    resource,
    url,
    requestPayload: options.requestPayload ?? null,
    httpStatus:
      typeof error?.status === 'number'
        ? error.status
        : 'no expuesto por el cliente Supabase',
    responseBody: error
      ? {
          code: error.code ?? null,
          message: error.message ?? null,
          details: error.details ?? null,
          hint: error.hint ?? null,
        }
      : (options.responseBody ?? null),
    verifiedIdentity: {
      sessionUserId: options.sessionUserId ?? null,
      profileId: options.profileId ?? null,
      profileActive: options.profileActive ?? null,
    },
  }

  if (error) {
    console.error('[TITÁN OS · Supabase]', payload)
    return
  }

  console.info('[TITÁN OS · Supabase]', payload)
}

export function describeSupabaseRequestError(
  resource: string,
  url: string,
  error: SupabaseDiagnosticError | null | undefined,
  options: Pick<
    SupabaseRequestDiagnosticOptions,
    | 'method'
    | 'stage'
    | 'requestPayload'
    | 'sessionUserId'
    | 'profileId'
    | 'profileActive'
  > = {},
) {
  const diagnostics = [
    `Etapa: ${options.stage ?? 'sin etapa'}`,
    `Método: ${options.method ?? 'GET'}`,
    `Recurso: ${resource}`,
    `URL: ${url}`,
    `Payload: ${JSON.stringify(options.requestPayload ?? null)}`,
    `HTTP: ${
      typeof error?.status === 'number'
        ? String(error.status)
        : 'no expuesto por el cliente Supabase'
    }`,
    `Código: ${error?.code ?? 'sin código'}`,
    `Mensaje: ${error?.message ?? 'sin mensaje'}`,
    `Detalle: ${error?.details ?? 'sin detalle'}`,
    `Sugerencia: ${error?.hint ?? 'sin sugerencia'}`,
    `Auth user UUID: ${options.sessionUserId ?? 'sin verificar'}`,
    `Profile UUID: ${options.profileId ?? 'sin verificar'}`,
    `Profile active: ${
      options.profileActive === null || options.profileActive === undefined
        ? 'sin verificar'
        : String(options.profileActive)
    }`,
  ]

  return diagnostics.join(' · ')
}
